import axios from "axios";
import { LOGIN_REQUEST, LOGIN_SUCCESS, LOGIN_ERROR, LOGOUT, MFA_REQUIRED } from "../types";
//import jwt_decode from "jwt-decode";
import { AAS_PREFIX, JWT_TOKEN } from "../../global/constants";
import setJWTToken from "../../securityUtils/setJWTToken";
import jwtDecode from "jwt-decode";
import { clearAllSessionData } from "../../securityUtils/cookieUtils";
import { clearAllPersistedJobs } from "../ws/JobPersistenceService";
import { cancelScheduleFollows } from "../schedule-activity-services/scheduleFollowRegistry";
import { clearSessionScopedStorage } from "../../global/storageScopePolicy";
import userPreferences from "../user-preferences/userPreferences";
import { discardLocalPreferences } from "../user-preferences/localPreferencesImport";

export const loginRequest = () => ({ type: LOGIN_REQUEST });
export const loginSuccess = (data) => ({
  type: LOGIN_SUCCESS,
  payload: data,
});
export const loginError = (err, ssoUrl = null) => ({
  type: LOGIN_ERROR,
  payload: err,
  ssoUrl,
});

// AAS refuses password sign-in for an « SSO only » tenant (SPEC-0076) with
// 403 { code: "SSO_REQUIRED", ssoUrl }. The login banner shows its own
// translated message and a link to the SSO sign-in.
export const SSO_REQUIRED = "SSO_REQUIRED";

// Only an https link without credentials, or a link on this site, from the answer is ever
// rendered. The browser parses "/\\host" and "//host" as another host, so paths are checked by origin.
export const safeSsoUrl = (url) => {
  if (typeof url !== "string") return null;
  try {
    if (/^https?:\/\//i.test(url)) {
      const parsed = new URL(url);
      if (parsed.username || parsed.password) return null;
      return parsed.protocol === "https:" || parsed.origin === window.location.origin ? url : null;
    }
    if (!url.startsWith("/")) return null;
    return new URL(url, window.location.origin).origin === window.location.origin ? url : null;
  } catch {
    return null;
  }
};

// Wipe every trace of the current user before a logout / user switch: the axios
// auth header, the identity localStorage keys, plus cookies & sessionStorage.
// The Redux store itself is reset by the LOGOUT action (see store/reducer).
//
// "username" and project-selection filters are intentionally NOT cleared here.
// They survive explicit logout so that a same-user re-login immediately recovers
// context. login() clears them only when switching to a different user.
const clearSession = () => {
  // A schedule follow left behind must not toast or refetch for the next session.
  cancelScheduleFollows({ logout: true });
  // Pending preference writes go out while the bearer is still attached; the
  // in-memory documents belong to this session only.
  userPreferences.flush();
  userPreferences.clear();
  setJWTToken(null);
  clearAllSessionData();
  // Progress-card buckets are owned by JobPersistenceService; every other
  // credential / tenant-bound key is cleared via the storage-scope policy.
  clearAllPersistedJobs();
  clearSessionScopedStorage();
};

// Clear per-user preferences/filters and identity — called when switching to a
// different user to prevent data leakage across accounts. (`username` survives a
// same-user logout for switch-detection, so it's removed explicitly here.)
const clearUserContextOnSwitch = () => {
  localStorage.removeItem("username");
  discardLocalPreferences();
};

// Logins are case-insensitive, so "Jane@x.com" today and "jane@x.com" tomorrow are the
// same account: an exact compare called that a user switch and wiped the column layouts.
const isSameUsername = (previous, current) =>
  !!previous &&
  !!current &&
  previous.trim().toLowerCase() === current.trim().toLowerCase();

const subOf = (token) => {
  try {
    return jwtDecode(token)?.sub;
  } catch (err) {
    return undefined;
  }
};

export const logout = () => {
  clearSession();
  return { type: LOGOUT };
};

export const login = (LoginRequest) => async (dispatch) => {
  // "username" survives explicit logout (clearSession leaves it) so we can
  // detect a user switch here and wipe context that must not leak across accounts.
  const previousUsername = localStorage.getItem("username");
  const isSameUser = isSameUsername(previousUsername, LoginRequest.username);

  dispatch(logout());

  if (!isSameUser) {
    clearUserContextOnSwitch();
  }

  dispatch(loginRequest());

  try {
    const res = await axios.post(`${AAS_PREFIX}/authenticate`, LoginRequest);
    const token = res.data.jwt;

    const decoded = jwtDecode(token); // { sub: "...", mfa: true?, ... }

    if (decoded.mfa) {
      dispatch({ type: MFA_REQUIRED, payload: token });
      return "MFA";
    }

    localStorage.setItem("username", LoginRequest.username);
    localStorage.setItem(JWT_TOKEN, token);
    setJWTToken(token);
    dispatch(loginSuccess());
    return "SUCCESS";
  } catch (err) {
    const data = err.response && err.response.data;
    if (err.response && err.response.status === 403 && data && data.code === SSO_REQUIRED) {
      dispatch(loginError(SSO_REQUIRED, safeSsoUrl(data.ssoUrl)));
      return "ERROR";
    }
    const errorMsg =
      err.response && err.response.status === 429
        ? "Too many connection attempts. Please try again later."
        : "Bad credentials. Please try again.";
    dispatch(loginError(errorMsg));
    return "ERROR";
  }
};

export const loginWithToken = (token) => async (dispatch) => {
  // SSO entry point — same clean-slate + user-switch semantics as password
  // login. Skipping the "username" bookkeeping here made every SSO re-login
  // look like a user switch, wiping the USER_BOUND preferences (column
  // layouts, filters) on each session.
  const previousUsername = localStorage.getItem("username");
  const username = subOf(token);
  const isSameUser = isSameUsername(previousUsername, username);

  dispatch(logout());

  if (!isSameUser) {
    clearUserContextOnSwitch();
  }

  dispatch(loginRequest());
  try {
    if (username) localStorage.setItem("username", username);
    localStorage.setItem(JWT_TOKEN, token);
    setJWTToken(token);
    dispatch(loginSuccess());
  } catch (err) {
    dispatch(loginError("SSO login failed"));
  }
};

export const verifyMfaCode = (code) => async (dispatch, getState) => {
  const tempToken = getState().security.tempToken;
  try {
    const res = await axios.post(`${AAS_PREFIX}/mfa/verify`, { code }, {
      headers: {
        Authorization: `Bearer ${tempToken}`,
      },
    });
    const finalToken = res.data.jwt;
    // login() stops at the MFA step before recording the username, so without this an MFA
    // account was never recognised at its next login and lost its column layouts every time.
    const username = subOf(finalToken) || subOf(tempToken);
    if (username) localStorage.setItem("username", username);
    localStorage.setItem(JWT_TOKEN, finalToken);
    setJWTToken(finalToken);
    dispatch({ type: LOGIN_SUCCESS });
  } catch (err) {
    dispatch({ type: LOGIN_ERROR, payload: "Invalid MFA code." });
  }
};


// An « SSO only » tenant refuses password recovery (SPEC-0076): the refusal is
// passed on so the form can say so; any other failure answers false.
const isSsoRequired = (err) =>
  err?.response?.status === 403 && err?.response?.data?.code === SSO_REQUIRED;

export const sendPasswordRecoveryEmail = (email) => async (dispatch) => {
  try {
    await axios.post(`${AAS_PREFIX}/users/forgot-password?email=${email}`);
    return true;
  } catch (err) {
    if (isSsoRequired(err)) throw err;
    return false;
  }
};

export const resetPassword = (request) => async (dispatch) => {
  try {
    await axios.post(`${AAS_PREFIX}/users/reset-password`, request);
    return true;
  } catch (err) {
    if (isSsoRequired(err)) throw err;
    return false;
  }
};
